import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileDown, Loader2, Mail, Printer, RefreshCw } from "lucide-react";
import { getSickNote, getMyAccess } from "@/lib/sick-notes.functions";
import { SickNoteDocument } from "@/components/admin/SickNoteDocument";
import { useSickNoteActions } from "@/components/admin/useSickNoteActions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { SickNote } from "@/lib/sick-notes";

export const Route = createFileRoute("/_authenticated/admin/sick-notes/$id")({
  head: () => ({ meta: [{ title: "Sick Note · Admin" }, { name: "robots", content: "noindex" }] }),
  component: ViewPage,
});

function ViewPage() {
  const { id } = Route.useParams();
  const getFn = useServerFn(getSickNote);
  const accessFn = useServerFn(getMyAccess);
  const actions = useSickNoteActions();
  const { data: access } = useQuery({ queryKey: ["sickNoteAccess"], queryFn: () => accessFn() });
  const { data, isPending, error } = useQuery({ queryKey: ["sickNote", id], queryFn: () => getFn({ data: { id } }) });
  const can = (p: string) => access?.perms.includes(p as never);

  if (isPending) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (error || !data) return <p className="text-sm text-destructive">{(error as Error)?.message ?? "Not found"}</p>;
  const note = data.note as SickNote;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="ghost" size="sm"><Link to="/admin/sick-notes"><ArrowLeft className="mr-2 h-4 w-4" />History</Link></Button>
        <span className="font-mono text-sm">{note.certificate_number}</span>
        <Badge variant={note.status === "issued" ? "default" : "secondary"}>{note.status === "issued" ? "Issued" : "Superseded"}</Badge>
        <div className="ml-auto flex flex-wrap gap-2">
          {can("sick_notes.pdf") && (
            <>
              <Button size="sm" onClick={() => actions.download(note)}><FileDown className="mr-2 h-4 w-4" />Download PDF</Button>
              <Button size="sm" variant="outline" onClick={() => actions.print(note)}><Printer className="mr-2 h-4 w-4" />Print</Button>
              {access?.admin && (
                <Button size="sm" variant="outline" onClick={() => actions.email(note)}><Mail className="mr-2 h-4 w-4" />Email patient</Button>
              )}
            </>
          )}
          {can("sick_notes.reissue") && note.status === "issued" && (
            <Button asChild size="sm" variant="outline"><Link to="/admin/sick-notes/new" search={{ reissue: note.id }}><RefreshCw className="mr-2 h-4 w-4" />Reissue</Link></Button>
          )}
        </div>
      </div>
      {note.status === "superseded" && (
        <p className="rounded-md border bg-muted/50 p-3 text-sm">This certificate was replaced by a reissued version. It is kept for the record and should not be sent out.</p>
      )}
      <div className="grid gap-4 xl:grid-cols-[1fr_340px]">
        <SickNoteDocument note={note} />
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Versions</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-sm">
              {data.versions.map((v: any) => (
                <Link key={v.id} to="/admin/sick-notes/$id" params={{ id: v.id }} className="flex justify-between rounded px-2 py-1 hover:bg-accent">
                  <span className="font-mono text-xs">{v.certificate_number} · v{v.version}</span>
                  <span className="text-xs text-muted-foreground">{v.status}</span>
                </Link>
              ))}
              {note.reissue_reason && <p className="pt-2 text-xs text-muted-foreground">Reissue reason: {note.reissue_reason}</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
